import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { FaGithub, FaReact, FaPython, FaNodeJs, FaEnvelope, FaLinkedin, FaHtml5, FaCss3Alt } from 'react-icons/fa';
import { SiTensorflow, SiPytorch, SiJavascript } from 'react-icons/si';
import { IoIosArrowUp } from 'react-icons/io';
import { useEffect, useState } from 'react';

const ProjectsContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  padding: 2rem;
  background-color: #FCFCFC;
`;

const ProjectsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const ProjectCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  display: flex;
  height: 280px;
  padding: 0;
  cursor: pointer;
`;

const ProjectImage = styled(motion.div)`
  width: 400px;
  height: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  display: flex;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
`;

const ProjectContent = styled.div`
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProjectTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const ProjectDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProjectFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TechStack = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TechIcon = styled.div`
  color: #007AFF;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PlayLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  background-color: #28a745;
  text-decoration: none;
  font-family: 'Space Grotesk', sans-serif;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin-right: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(40, 167, 69, 0.2);

  &:hover {
    background-color: #218838;
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(40, 167, 69, 0.3);
  }
`;

const GithubLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #333;
  text-decoration: none;
  font-family: 'Space Grotesk', sans-serif;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: transparent;
  transition: all 0.3s ease;

  &:hover {
    color: #007AFF;
    background: rgba(0, 122, 255, 0.1);
  }
`;

const Footer = styled.footer`
  background-color: #f8f9fa;
  padding: 2rem 0;
  margin-top: 4rem;
  border-top: 1px solid #eaeaea;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
    text-align: center;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  text-decoration: none;
  font-family: 'Space Grotesk', sans-serif;
  transition: color 0.3s ease;

  &:hover {
    color: #007AFF;
  }
`;

const ScrollToTop = styled(motion.button)`
  background-color: #FFB100;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 177, 0, 0.2);

  &:hover {
    background-color: #FFC233;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 177, 0, 0.3);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const IntroSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem 2rem;
`;

const IntroText = styled(motion.p)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.4rem;
  line-height: 1.6;
  color: #333;
  max-width: 900px;
  margin: 0 auto;
`;

const ComingSoonText = styled(motion.p)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.2rem;
  color: #666;
  text-align: center;
  margin: 3rem 0 1rem;
  font-style: italic;
`;

const ImageModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
`;

const ModalImage = styled(motion.img)`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Projects = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedImage, setSelectedImage] = useState(null);

  const projects = [
    {
      id: 1,
      title: "Unlimited Wordle Game",
      description: "A web-based implementation of the popular word guessing game . It turns out people can be very productive when have a pile of paper to read. Into Wordle game recent, build one to play.  Built with vanilla JavaScript, HTML, and CSS.",
      image: "/wordle.png",
      techStack: ["javascript", "html", "css"],
      githubUrl: "https://github.com/yachi1117/wordle"
    },
    {
      id: 2,
      title: "Parking Management System",
      description: "A comprehensive parking management system built with FastAPI, MySQL, and modern frontend technologies. Features include user management, parking lot query, vehicle entry/exit tracking, and an automatic billing system.",
      image: "https://github.com/yachi1117/carParkProj/raw/main/api.png",
      techStack: ["python", "javascript", "html", "css"],
      githubUrl: "https://github.com/yachi1117/carParkProj"
    }
  ];

  const getTechIcon = (tech) => {
    switch (tech) {
      case "react":
        return <FaReact title="React" />;
      case "python":
        return <FaPython title="Python" />;
      case "pytorch":
        return <SiPytorch title="PyTorch" />;
      case "tensorflow":
        return <SiTensorflow title="TensorFlow" />;
      case "node":
        return <FaNodeJs title="Node.js" />;
      case "html":
        return <FaHtml5 title="HTML5" />;
      case "css":
        return <FaCss3Alt title="CSS3" />;
      case "javascript":
        return <SiJavascript title="JavaScript" />;
      default:
        return null;
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <ProjectsContainer>
      <IntroSection>
        <IntroText
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          I've worked on a range of hands-on projects that reflect both my curiosity and commitment to learning. These include full-stack applications and data related projects — each one helping me grow as a developer and thinker. Here's a glimpse into what I've been building, learning, and refining along the way.
        </IntroText>
      </IntroSection>
      <ProjectsGrid>
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{
              scale: 1.02,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.98 }}
          >
            <ProjectImage
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
              onClick={() => setSelectedImage(project.image)}
              style={{ cursor: 'zoom-in' }}
            >
              <motion.img 
                src={project.image} 
                alt={project.title}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x280?text=Project+Preview';
                }}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.5 }}
              />
            </ProjectImage>
            <ProjectContent>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
              </motion.div>
              <ProjectFooter>
                <TechStack>
                  {project.techStack.map((tech, index) => (
                    <TechIcon 
                      key={tech}
                      as={motion.div}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      whileHover={{ 
                        scale: 1.2,
                        rotate: 10,
                        transition: { duration: 0.2 }
                      }}
                    >
                      {getTechIcon(tech)}
                    </TechIcon>
                  ))}
                </TechStack>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  {project.id === 1 && (
                    <PlayLink 
                      href="https://www.yachichen.com/wordle"
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <motion.span
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                      >
                        ▶
                      </motion.span>
                      Play
                    </PlayLink>
                  )}
                  <GithubLink 
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <FaGithub />
                    </motion.div>
                    View on GitHub
                  </GithubLink>
                </div>
              </ProjectFooter>
            </ProjectContent>
          </ProjectCard>
        ))}
      </ProjectsGrid>
      {selectedImage && (
        <ImageModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => setSelectedImage(null)}
        >
          <ModalImage
            src={selectedImage}
            alt="Project Preview"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          />
          <CloseButton
            onClick={() => setSelectedImage(null)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            ×
          </CloseButton>
        </ImageModal>
      )}
      <ComingSoonText
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        Further projects are currently under preparation for upload.
      </ComingSoonText>
      <Footer>
        <FooterContent>
          <ContactInfo>
            <ContactLink 
              href="mailto:ychen10001@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaEnvelope />
              ychen10001@gmail.com
            </ContactLink>
            <ContactLink 
              href="https://www.linkedin.com/in/yaqi-c-781b8729a/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaLinkedin />
              LinkedIn
            </ContactLink>
          </ContactInfo>
          <ScrollToTop
            onClick={scrollToTop}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <IoIosArrowUp size={24} />
          </ScrollToTop>
        </FooterContent>
      </Footer>
    </ProjectsContainer>
  );
};

export default Projects; 